import type {
  DocumentGenerationProgressDTO,
  DocumentGenerationStatusResponseDTO,
} from '@shared/schemas/document-generation';

export type GenerationStepKey = 'plan' | 'research' | 'write' | 'format' | 'ready';
export type GenerationStepState = 'done' | 'active' | 'todo';

export type GenerationStep = {
  key: GenerationStepKey;
  label: string;
  state: GenerationStepState;
};

export type GenerationProgressView = {
  /** Étape en cours, formulée pour l'utilisateur (« Rédaction 3/8 », « Recherche web »). */
  currentLabel: string;
  /** Compteur de sections rédigées, absent hors de l'étape de rédaction. */
  counter: { done: number; total: number } | null;
  /** 0–100, monotone pour un même moteur : alimente la barre. */
  percent: number;
  /** Libellé de chargement (shimmer) propre à l'étape en cours, absent quand le libellé de l'étape suffit. */
  shimmerLabel: string | null;
  steps: GenerationStep[];
};

export type GenerationProgressOptions = {
  /**
   * La génération est passée (ou passe) par l'étape « Recherche ». Le statut ne dit pas d'avance si la
   * recherche web aura lieu : l'appelant mémorise l'avoir vue (`useResearchStageSeen`). Le stage courant
   * `research` suffit à lui seul à afficher l'étape.
   */
  withResearch?: boolean;
};

const STEP_ORDER: GenerationStepKey[] = ['plan', 'write', 'format', 'ready'];
const STEP_ORDER_WITH_RESEARCH: GenerationStepKey[] = ['plan', 'research', 'write', 'format', 'ready'];

const STEP_LABELS: Record<GenerationStepKey, string> = {
  format: 'Mise en forme',
  plan: 'Plan',
  ready: 'Prêt',
  research: 'Recherche',
  write: 'Rédaction',
};

/** Libellé accessible (annonce vocale, `aria-valuetext`) de l'étape de recherche. */
const RESEARCH_ANNOUNCEMENT = 'Recherche web';
/** Libellé de chargement affiché en shimmer pendant la recherche. */
export const RESEARCH_SHIMMER_LABEL = 'Recherche de sources…';

/**
 * « Recherche · 12 sources lues », ou « Recensement des acteurs · 6 sources lues » pendant la
 * découverte qui précède le plan : absent tant qu'aucune page n'a été lue.
 */
export function formatResearchProgressLabel(research: DocumentGenerationProgressDTO['research']) {
  const pagesRead = research?.pagesRead ?? 0;
  if (pagesRead <= 0) {
    return null;
  }
  const label = research?.phase === 'discovery' ? 'Recensement des acteurs' : STEP_LABELS.research;
  return `${label} · ${pagesRead} ${pagesRead > 1 ? 'sources lues' : 'source lue'}`;
}

function resolveActiveKey(
  progress: DocumentGenerationProgressDTO | undefined,
  status: DocumentGenerationStatusResponseDTO['status'] | undefined,
): GenerationStepKey {
  if (status === 'succeeded') {
    return 'ready';
  }
  switch (progress?.stage) {
    case 'research':
      return 'research';
    case 'sections':
      return 'write';
    case 'finalizing':
    case 'rendering':
      return 'format';
    case 'done':
      return 'ready';
    default:
      return 'plan';
  }
}

// La barre ne recule jamais : plan < recherche < rédaction < mise en forme < prêt. Quand la recherche
// a eu lieu, la rédaction démarre plus haut pour lui laisser un segment (6 → 12 → 16).
const WRITE_START = 10;
const WRITE_START_AFTER_RESEARCH = 16;
const WRITE_END = 80;
const RESEARCH_PERCENT = 12;
const RESEARCH_PERCENT_SPAN = 3;

function resolvePercent(
  progress: DocumentGenerationProgressDTO | undefined,
  activeKey: GenerationStepKey,
  withResearch: boolean,
): number {
  switch (activeKey) {
    case 'ready':
      return 100;
    case 'plan':
      return progress?.stage === 'outline' ? 6 : 2;
    case 'research': {
      // Le segment de recherche avance avec les pages lues, sans jamais atteindre la rédaction.
      const target = progress?.research?.target ?? 0;
      const ratio = target > 0 ? Math.min((progress?.research?.pagesRead ?? 0) / target, 1) : 0;
      return RESEARCH_PERCENT + Math.floor(RESEARCH_PERCENT_SPAN * ratio);
    }
    case 'write': {
      const total = Math.max(progress?.totalSections ?? 0, 1);
      const done = Math.min(progress?.completedSections ?? 0, total);
      const start = withResearch ? WRITE_START_AFTER_RESEARCH : WRITE_START;
      return start + Math.round(((WRITE_END - start) * done) / total);
    }
    default:
      return progress?.stage === 'rendering' ? 94 : 86;
  }
}

export function buildGenerationProgress(
  progress: DocumentGenerationProgressDTO | undefined,
  status?: DocumentGenerationStatusResponseDTO['status'],
  options: GenerationProgressOptions = {},
): GenerationProgressView {
  const activeKey = resolveActiveKey(progress, status);
  // L'étape n'est jamais retirée une fois affichée : `research` courant force son inclusion.
  const withResearch = Boolean(options.withResearch) || activeKey === 'research';
  const order = withResearch ? STEP_ORDER_WITH_RESEARCH : STEP_ORDER;
  const activeIndex = order.indexOf(activeKey);
  const total = progress?.totalSections ?? 0;
  const done = Math.min(progress?.completedSections ?? 0, total);
  const counter = activeKey === 'write' && total > 0 ? { done, total } : null;
  const researchLabel = activeKey === 'research' ? formatResearchProgressLabel(progress?.research) : null;
  const steps = order.map((key, index): GenerationStep => ({
    key,
    label: key === 'research' && researchLabel ? researchLabel : STEP_LABELS[key],
    // L'étape finale est « faite » dès qu'on l'atteint : il n'y a plus rien à attendre.
    state: index < activeIndex || (index === activeIndex && key === 'ready')
      ? 'done'
      : index === activeIndex ? 'active' : 'todo',
  }));
  const currentLabel = activeKey === 'research'
    ? RESEARCH_ANNOUNCEMENT
    : counter && activeKey === 'write' ? `Rédaction ${counter.done}/${counter.total}` : STEP_LABELS[activeKey];

  return {
    counter,
    currentLabel,
    percent: resolvePercent(progress, activeKey, withResearch),
    shimmerLabel: activeKey === 'research' ? researchLabel ?? RESEARCH_SHIMMER_LABEL : null,
    steps,
  };
}

/** Le plan existe : le canvas peut s'ouvrir et montrer les sections en squelette. */
export function hasGenerationPlan(progress: DocumentGenerationProgressDTO | undefined) {
  if (!progress) {
    return false;
  }
  return progress.modelRevision > 0
    || progress.totalSections > 0
    || progress.stage === 'research'
    || progress.stage === 'sections'
    || progress.stage === 'finalizing'
    || progress.stage === 'rendering'
    || progress.stage === 'done';
}

/** Libellé de la feuille en squelette tant que l'aperçu n'est pas disponible. */
export function resolveCanvasPlaceholderLabel(progress: DocumentGenerationProgressDTO | undefined) {
  return progress?.stage === 'research' ? RESEARCH_SHIMMER_LABEL : 'Préparation du plan…';
}
